import Image from 'next/image'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'

/* assets */
import { ReactComponent as ChevronRight } from 'public/assets/icons/chevron-right.svg'

/* styling */
import styles from './_.module.css'

interface Props {
  title?: string
  description?: string
  class?: string
  mode?: 'light' | 'dark'
  list: {
    id: string | number
    icon?: StrapiImageFile
    tag?: string | null
    title: string
    description: string
    image?: StrapiImageFile
    link?: StrapiLinkSharedComponent | null
    videoUrl?: string
    bgColor: string | null
  }[]
}

function Features({
  title,
  description,
  list,
  mode,
  ...props
}: Props): JSX.Element {
  return (
    <section
      className={clsx(
        mode === 'dark'
          ? 'bg-blue-web-90 text-white'
          : 'bg-yellow-10 text-blue-web-90',
        props.class
      )}
    >
      <div className="container | py-10 md:py-18">
        <header>
          <h2
            className={clsx(
              mode === 'dark' ? 'text-white' : 'text-gray-90',
              'font-heading text-3xl md:text-5xl font-medium tracking-tight | max-w-col-8',
              description ? 'mb-4' : ''
            )}
          >
            {title}
          </h2>
          {description ? (
            <div
              className={clsx(
                mode === 'dark' ? 'text-white' : 'text-gray-70',
                'font-body font-normal text-xl '
              )}
              dangerouslySetInnerHTML={{ __html: description }}
            ></div>
          ) : null}
        </header>
      </div>
      <div className="md:container">
        {list.map((item, index) => (
          <div
            key={item.id}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center | md:pb-18"
          >
            <div
              className={clsx(
                'md:col-span-5 px-5 md:px-0 order-2',
                index === 1
                  ? 'md:order-2 md:justify-self-start md:col-start-8 md:col-end-13'
                  : 'md:order-1 md:justify-self-end'
              )}
            >
              {item.tag ? (
                <div className="flex items-center mb-6">
                  <span
                    className="w-8 h-8 rounded-md"
                    style={{ background: item.bgColor ?? '' }}
                  />
                  <p className="font-heading text-2xl text-gray-90 tracking-widest font-medium ml-4">
                    {item.tag}
                  </p>
                </div>
              ) : null}

              <h3
                className={clsx(
                  'font-heading',
                  mode === 'dark'
                    ? 'text-3xl lg:text-4xl font-medium | mb-6'
                    : 'text-3xl lg:text-4xl font-medium text-gray-90 | mb-6'
                )}
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
              <div
                className={clsx(
                  styles.description,
                  mode === 'dark'
                    ? 'text-lg lg:text-xl | mb-9'
                    : 'text-lg lg:text-xl text-gray-60 | mb-9'
                )}
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
              {item.link ? (
                <div
                  className={clsx(
                    mode === 'dark'
                      ? 'text-base font-medium'
                      : 'text-base font-medium text-blue-50'
                  )}
                >
                  <Link
                    href={item.link.href}
                    target={item.link.title}
                    className="flex items-center underline underline-offset-8"
                  >
                    {item.link.title}
                    <ChevronRight className="ml-4" />
                  </Link>
                </div>
              ) : null}
            </div>

            <div
              className={clsx(
                `md:col-span-6 order-1 | md:justify-self-stretch md:rounded-2xl`,
                index === 1
                  ? 'md:order-1 md:justify-self-start'
                  : 'md:order-2 md:justify-self-end md:col-start-7 md:col-end-13'
              )}
              style={{ background: item.bgColor ?? '' }}
            >
              {item.image ? (
                <Image
                  src={item.image.url}
                  alt={item.image.alternativeText ?? ''}
                  width={item.image.width}
                  height={item.image.height}
                  className="px-8 md:px-4 mx-auto"
                />
              ) : (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="px-8 md:px-4 mx-auto"
                >
                  <source src={item.videoUrl} type="video/mp4" />
                  <source src={item.videoUrl} type="video/webm" />
                </video>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default Features
